<!--
  @description: 分享对话的对话框
-->
<template>
  <el-dialog v-model="visible" title="分享会话" width="560px" :before-close="handleClose" :z-index="2000">
    <div v-if="session" class="share-dialog">
      <!-- 会话信息 -->
      <div class="share-session-info">
        <div class="info-label">会话标题</div>
        <div class="info-value">{{ session.title }}</div>
      </div>

      <!-- 创建分享 -->
      <div class="share-create-block">
        <div class="block-title">创建新分享</div>
        <div class="expire-row">
          <span class="expire-label">有效期：</span>
          <el-radio-group v-model="expiresIn" size="small">
            <el-radio-button value="1d">24 小时</el-radio-button>
            <el-radio-button value="7d">7 天</el-radio-button>
            <el-radio-button value="30d">30 天</el-radio-button>
            <el-radio-button value="permanent">永久</el-radio-button>
          </el-radio-group>
        </div>

        <div v-if="generatedUrl" class="generated-url-row">
          <el-input :model-value="generatedUrl" readonly size="large">
            <template #append>
              <el-button @click="copyGeneratedUrl">复制</el-button>
            </template>
          </el-input>
          <div class="generated-tip">
            ✅ 链接已生成，任何拿到链接的人都可以查看本对话的快照。
          </div>
        </div>

        <el-button v-else type="success" :loading="creating" @click="handleCreate">
          生成分享链接
        </el-button>
      </div>

      <!-- 已有分享列表 -->
      <div class="share-list-block">
        <div class="block-title">
          <span>已有分享</span>
          <el-button size="small" link :loading="listLoading" @click="loadShares">
            <el-icon>
              <Refresh />
            </el-icon>
          </el-button>
        </div>

        <el-table v-loading="listLoading" :data="shareList" empty-text="暂无分享" size="small" style="width: 100%">
          <el-table-column label="创建时间" min-width="130">
            <template #default="{ row }">
              {{ formatDateTime(row.createdAt) }}
            </template>
          </el-table-column>
          <el-table-column label="过期时间" min-width="130">
            <template #default="{ row }">
              {{ row.expiresAt ? formatDateTime(row.expiresAt) : '永久' }}
            </template>
          </el-table-column>
          <el-table-column label="状态" width="80">
            <template #default="{ row }">
              <el-tag :type="statusTagType(row.status)" size="small">
                {{ statusText(row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="140" align="right">
            <template #default="{ row }">
              <el-button size="small" link type="primary" @click="copyShareUrl(row.id)">
                复制链接
              </el-button>
              <el-button v-if="row.status === 'active'" size="small" link type="danger" @click="handleRevoke(row.id)">
                取消分享
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <template #footer>
      <el-button @click="handleClose">关闭</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { copyToClipboard } from '~/utils/index'
import { Refresh } from '@element-plus/icons-vue'

interface SessionInfo {
  id: string
  title: string
}

interface ShareRow {
  id: string
  sessionId: string
  title: string
  createdAt: number
  expiresAt: number | null
  status: 'active' | 'revoked' | 'expired'
}

const props = defineProps<{
  modelValue: boolean
  session: SessionInfo | null
  userId: string
}>()

const emit = defineEmits<{
  'update:modelValue': [v: boolean]
}>()

const visible = ref(props.modelValue)

watch(() => props.modelValue, (v) => {
  visible.value = v
  if (v) {
    // 重置每次打开时的临时状态
    expiresIn.value = '7d'
    generatedUrl.value = ''
    loadShares()
  }
})
watch(visible, (v) => emit('update:modelValue', v))

const expiresIn = ref<'1d' | '7d' | '30d' | 'permanent'>('7d')
const creating = ref(false)
const generatedUrl = ref('')

const shareList = ref<ShareRow[]>([])
const listLoading = ref(false)

/**
 * 生成分享链接
 * @param shareId 
 */
const buildShareUrl = (shareId: string) => {
  if (typeof window === 'undefined') return `/share/${shareId}`
  return `${window.location.origin}/share/${shareId}`
}

/**
 * 加载分享列表
 */
const loadShares = async () => {
  if (!props.session || !props.userId) return
  listLoading.value = true
  try {
    const res: any = await $fetch('/api/share/list', {
      method: 'GET',
      query: { userId: props.userId, sessionId: props.session.id }
    })
    if (res.success) {
      shareList.value = res.shares
    } else {
      ElMessage.error(res.errorMessage || '加载分享列表失败')
    }
  } catch (err) {
    console.error(err)
    ElMessage.error('加载分享列表失败')
  } finally {
    listLoading.value = false
  }
}

/**
 * 创建分享
 */
const handleCreate = async () => {
  if (!props.session) return
  creating.value = true
  try {
    const res: any = await $fetch('/api/share/create', {
      method: 'POST',
      body: {
        sessionId: props.session.id,
        userId: props.userId,
        expiresIn: expiresIn.value
      }
    })
    if (res.success) {
      generatedUrl.value = buildShareUrl(res.shareId)
      ElMessage.success('分享链接已生成')
      await loadShares()
    } else {
      ElMessage.error(res.errorMessage || '创建分享失败')
    }
  } catch (err) {
    console.error(err)
    ElMessage.error('创建分享失败')
  } finally {
    creating.value = false
  }
}

/**
 * 复制刚生成的分享链接
 */
const copyGeneratedUrl = () => {
  copyToClipboard(generatedUrl.value)
}

/**
 * 复制已有的分享链接
 * @param shareId 
 */
const copyShareUrl = (shareId: string) => {
  copyToClipboard(buildShareUrl(shareId))
}

/**
 * 取消分享链接
 * @param shareId 
 */
const handleRevoke = async (shareId: string) => {
  try {
    await ElMessageBox.confirm(
      '取消后该链接立即失效，访客无法再访问。确认要取消吗？',
      '取消分享',
      {
        confirmButtonText: '确认取消',
        cancelButtonText: '再想想',
        type: 'warning'
      }
    )
  } catch {
    return
  }

  try {
    const res: any = await $fetch('/api/share/revoke', {
      method: 'POST',
      body: { shareId, userId: props.userId }
    })
    if (res.success) {
      ElMessage.success('分享已取消')
      await loadShares()
    } else {
      ElMessage.error(res.errorMessage || '取消分享失败')
    }
  } catch (err) {
    console.error(err)
    ElMessage.error('取消分享失败')
  }
}

/**
 * 格式化时间
 * @param ts 
 */
const formatDateTime = (ts: number) => {
  if (!ts) return ''
  const d = new Date(ts)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/**
 * 分享状态标签类型: 有效、已过期、已取消
 * @param s 
 */
const statusTagType = (s: ShareRow['status']) => {
  if (s === 'active') return 'success'
  if (s === 'expired') return 'info'
  return 'danger'
}

/**
 * 分享状态文本: 有效、已过期、已取消
 * @param s 状态枚举值
 */
const statusText = (s: ShareRow['status']) => {
  if (s === 'active') return '有效'
  if (s === 'expired') return '已过期'
  return '已取消'
}

const handleClose = () => {
  visible.value = false
}
</script>

<style scoped lang="scss">
.share-dialog {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.share-session-info {
  background: #f7f8fa;
  border-radius: 6px;
  padding: 10px 14px;

  .info-label {
    font-size: 12px;
    color: #909399;
    margin-bottom: 4px;
  }

  .info-value {
    font-size: 14px;
    color: #303133;
    word-break: break-all;
  }
}

.block-title {
  font-size: 13px;
  color: #606266;
  font-weight: 600;
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.share-create-block {
  .expire-row {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
  }

  .expire-label {
    font-size: 13px;
    color: #606266;
    margin-right: 8px;
    flex-shrink: 0;
  }
}

.generated-url-row {
  .generated-tip {
    font-size: 12px;
    color: #67c23a;
    margin-top: 6px;
  }
}

.share-list-block {
  border-top: 1px solid #ebeef5;
  padding-top: 16px;
}
</style>
